"use client";

import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { cn } from "lib/utils";

/** ----------------------------------------------------------------
 * Select (Root)
 * ----------------------------------------------------------------*/
function Select(props: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

/** ----------------------------------------------------------------
 * Trigger
 * - Looks like your inputs: white border surface, dark-safe, focus ring
 * - size: "sm" | "default"
 * ----------------------------------------------------------------*/
function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: "sm" | "default";
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        // base surface (uses your dark overrides for ., borders, etc.)
        " border border-line-strong rounded-lg text-sm shadow-xs",
        "inline-flex items-center justify-between gap-2 whitespace-nowrap",
        "px-3 py-2 transition-[color,box-shadow,border-color] smooth-transition",
        "hover:border-gray-400",
        "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        // heights
        "data-[size=default]:h-9 data-[size=sm]:h-8",
        // icon color
        "[&_svg:not([class*='text-'])]:text-content-muted [&_svg:not([class*='size-'])]:size-4",
        // dark theming (leverages your .dark . overrides too)
        "dark:bg-dark-700 dark:border-dark-500 dark:hover:border-dark-400",
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="size-4 opacity-70" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

/** ----------------------------------------------------------------
 * Value (inside Trigger)
 * ----------------------------------------------------------------*/
function SelectValue(
  props: React.ComponentProps<typeof SelectPrimitive.Value>
) {
  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      className="line-clamp-1 flex items-center gap-2 text-content dark:text-gray-100"
      {...props}
    />
  );
}

/** ----------------------------------------------------------------
 * Content (dropdown)
 * - White card + border (auto dark)
 * - Popper positioning, transitions, invisible scrollbar
 * ----------------------------------------------------------------*/
function SelectContent({
  className,
  children,
  position = "popper",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          "bg-surface text-content dark:bg-dark-800 dark:text-gray-100 border border-line dark:border-dark-600 rounded-xl shadow-lg",
          "relative z-[150] min-w-[8rem] overflow-hidden",
          // radix animations
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[side=bottom]:slide-in-from-top-2",
          "data-[side=left]:slide-in-from-right-2",
          "data-[side=right]:slide-in-from-left-2",
          "data-[side=top]:slide-in-from-bottom-2",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className
        )}
        position={position}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={cn(
            "p-1 max-h-[280px] overflow-y-auto",
            position === "popper" &&
              "w-full min-w-[var(--radix-select-trigger-width)]"
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

/** ----------------------------------------------------------------
 * Group / Label / Separator
 * ----------------------------------------------------------------*/
function SelectGroup(
  props: React.ComponentProps<typeof SelectPrimitive.Group>
) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectLabel(
  { className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>
) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn("px-2 py-1.5 text-xs text-content-muted dark:text-gray-400", className)}
      {...props}
    />
  );
}

function SelectSeparator(
  { className, ...props }: React.ComponentProps<typeof SelectPrimitive.Separator>
) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("bg-gray-200 dark:bg-dark-600 -mx-1 my-1 h-px pointer-events-none", className)}
      {...props}
    />
  );
}

/** ----------------------------------------------------------------
 * Item
 * - Active state = blue background (your brand)
 * - Hover states match your dark/hover tokens
 * - `label` prop: text shown in the trigger after selection (defaults to children if string)
 * - children: full row content rendered in the dropdown (supports flex layouts)
 * ----------------------------------------------------------------*/
function SelectItem({
  className,
  children,
  label,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item> & { label?: string }) {
  // Determine the value to show in the trigger when this item is selected.
  // Radix reads ItemText content for the trigger display — we hide it visually
  // but keep it in the DOM so Radix can read it.
  const triggerLabel = label ?? (typeof children === "string" ? children : undefined);

  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-md py-1.5 pl-2 pr-8 text-sm outline-none",
        "text-content dark:text-gray-100",
        "smooth-transition",
        "hover:bg-gray-100 dark:hover:bg-dark-700",
        "focus:bg-blue-600 focus:text-white dark:focus:bg-blue-600 dark:focus:text-white",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      )}
      {...props}
    >
      {/* Hidden ItemText for trigger display — Radix reads this for the selected value label */}
      {triggerLabel !== undefined && (
        <span style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>
          <SelectPrimitive.ItemText>{triggerLabel}</SelectPrimitive.ItemText>
        </span>
      )}
      {/* Visible row content — full width, supports any layout */}
      <span className="flex-1 flex items-center min-w-0">{children}</span>
      {/* Checkmark indicator */}
      <span className="absolute right-2 flex size-3.5 items-center justify-center flex-shrink-0">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
    </SelectPrimitive.Item>
  );
}

/** ----------------------------------------------------------------
 * Scroll buttons
 * ----------------------------------------------------------------*/
function SelectScrollUpButton(
  { className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>
) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn("flex cursor-default items-center justify-center py-1", className)}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton(
  { className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>
) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn("flex cursor-default items-center justify-center py-1", className)}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
